import { useCallback, useEffect, useState } from "react";
import type { CSSProperties, KeyboardEvent, ReactElement, RefObject } from "react";
import type { DueItem, Unternehmen } from "@kundeninfo/api-client";
import { Icon, useSofortPopup } from "@kundeninfo/ui";
import { Avatar as MeAvatar, Chip } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { DueSymbol } from "../termine/DueSourceChips.js";
import { DUE_ITEM_TEXT } from "../termine/dueItemTexts.js";
import { Avatar, useOhneTipp } from "./Avatar.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import { wandereFokus } from "./TicketZeitleiste.js";
import { AXIS_PX, HEADER_PX, LABEL_PX, LABEL_PX_PHONE, ROW_MIN_PX } from "./timelineModel.js";
import type { layout, TimelineGroup, TimelinePerson, TimelineRow } from "./timelineModel.js";
import { TIMELINE_TEXT } from "./timelineTexts.js";
import "./TicketTimeline.css";

/**
 * DIE ZEILENSPALTE DER ZEITACHSE (Spec Plan-Ansicht S5, S6, S8, S13, S15; Übergang T-14) — eine Gruppe als DOM: der
 * Gruppenkopf (HEADER_PX hoch) ist ein Klappknopf mit Kürzel, Zähler und „n überfällig“; offen folgt je Zeile ein
 * Listeneintrag mit GENAU der Höhe aus `layout()` (Review Focus 2: die Zeichenfläche rechnet mit denselben Zahlen).
 * Zahlen und „frei“ kommen aus dem geladenen Stand (`loaded`/`loadedOverdue`), „+n“ aus `hidden` (Ruling M2, A10).
 * Maße (Kopfhöhe, Klebekante, Spaltenbreite, Einrückung) kommen aus den Modellkonstanten, nie aus dem CSS.
 *
 * Tastatur (S15, Review Focus 4): eine Liste mit wanderndem Tabstopp (`wandereFokus` über ALLE Gruppen der Seite):
 * Pfeil hoch/runter und Pos1/Ende wechseln die Zeile über die Gruppenköpfe hinweg; Pfeil rechts/links wandert durch
 * `row.bars` (die sichtbaren Balken in Rangfolge S10) und sagt den Balken über die Live-Region der Ansicht an. Hinter
 * dem letzten Balken ist „+n“ ein eigener Halt: angesagt mit den Nummern, Enter öffnet dort an einer Personenzeile die
 * Liste (der Knopf selbst ist kein Tab-Halt). Enter öffnet sonst den gewählten Balken, die Leertaste klappt einen
 * Eltern-Balken (nur dann abgefangen), Escape räumt die Auswahl. Mit Alt, Strg oder Meta bleibt jede Taste beim Browser.
 * Die Auswahl selbst hält die Ansicht (`selection`); hier stehen nur der Tabstopp und der „+n“-Halt — der gilt, bis die
 * Zeile verlassen wird (Pfeil hoch/runter oder eine andere Auswahl der Ansicht). Wiederholungen (533) sagen „auch unter
 * ‹Hauptteam›“, wenn die Ansicht `groupNames` reicht; die eigene Zeile trägt mit `meId` den „ich“-Rand.
 *
 * Terminebene (Spec D24): Gruppen „Meilensteine“ und je Quelle tragen im Kopf Symbol, Wort, Zahl und „n überfällig“ —
 * „+ Meilenstein“ steht nicht im Kopf, sondern einmal in der Quellenzeile der Ansicht (Ruling D24, Aufgabe 8). Ihre Zeilen
 * (`DUE`, je Unternehmen) zeigen das Kürzel in der Hausfarbe; Pfeil rechts/links wandert durch `row.markers` in Rangfolge und sagt
 * den Eintrag an (`DUE_ITEM_TEXT.speak`), Enter öffnet ihn (`onOpenDue`), „+n“ ist dort ein Kennzeichen.
 */

/** `barIndex` zählt in `row.bars`; `null` = Zeile gewählt, kein Balken. */
export type TimelineSelection = { rowId: string; barIndex: number | null };
export type TicketTimelineRowsProps = {
  group: TimelineGroup; layout: ReturnType<typeof layout>; phone: boolean; heute: string;
  selection: TimelineSelection | null;
  /** Pfeile wählen Zeile bzw. Balken, Escape `null`; nach einem Neuladen mit weniger Balken die geklemmte Wahl. */
  onSelect: (s: TimelineSelection | null) => void;
  /** Enter am gewählten Balken. */
  onOpen: (ticketId: string) => void;
  /** Leertaste am gewählten Eltern-Balken (nur `canExpand`). */
  onToggleBar: (ticketId: string) => void;
  /** Klick auf den Gruppenkopf. */
  onToggleGroup: (groupId: string) => void;
  /** „+n“ an einer Personenzeile (Klick oder Enter am „+n“-Halt) → Liste mit Filter Zuständig = Person. */
  onMore: (person: TimelinePerson) => void;
  /** Die `aria-live`-Region der Ansicht (Vorlesestimme der Balkenwanderung). */
  liveRef: RefObject<HTMLElement | null>;
  /** Name je Team-Kennung: die Vorlesestimme einer schraffierten Wiederholung sagt „auch unter ‹Hauptteam›“ (533). */
  groupNames?: ReadonlyMap<string, string>;
  /** Der Angemeldete: sein Avatar trägt den „ich“-Rand (`eigen`, F48). */
  meId?: string;
  /** Enter an einer gewählten Marke (D24): Meilenstein → Panel, sonst Sprung — die Ansicht entscheidet. */
  onOpenDue: (item: DueItem) => void;
  /** Hausfarbe des Kürzels an Terminzeilen. */
  companies?: readonly Pick<Unternehmen, "shortCode" | "colorHex">[];
};

/** Die Zeilen ALLER Gruppen — der Tastaturweg läuft über die Gruppengrenzen (Köpfe und zugeklappte Gruppen dazwischen
 *  haben keinen Listeneintrag). */
const ROW = ".tl__zeilen > li[tabindex]";
/** Einrückung je Tiefe einer Unterzeile (S13, Telefon wie Desktop). */
const INDENT_PX = 12;

/** Was die Tastatur einer Zeile durchwandert: Balken bzw. an Terminzeilen die Marken (D24), sichtbar in Rangfolge. */
const stops = (row: TimelineRow): readonly { id: string }[] => (row.kind === "DUE" ? row.markers : row.bars);
/** Die Nummern hinter „+n“: Anzeige-Nummern der Tickets bzw. Nummer (sonst Titel) der Einträge. */
const hiddenNumbers = (row: TimelineRow): string[] => (row.kind === "DUE"
  ? row.hiddenMarkers.map((m) => m.item.reference ?? m.item.title) : row.hidden.map((b) => b.card.displayNumber));

/** Der gewählte Halt einer Zeile als Index in `row.bars` bzw. `row.markers`; nach einem Neuladen mit weniger Halten auf
 *  den letzten geklemmt, ohne Halt `null` (G5). */
function barAt(row: TimelineRow, s: TimelineSelection | null): number | null {
  const n = stops(row).length;
  return s?.rowId !== row.id || s.barIndex === null || n === 0 ? null : Math.min(s.barIndex, n - 1);
}

export function TicketTimelineRows(p: TicketTimelineRowsProps): ReactElement {
  const { group, phone, selection } = p;
  const rows = group.rows;
  const [active, setActive] = useState<string | null>(null);
  /** Der „+n“-Halt: in dieser Zeile hinter dem gewählten Balken angesagt, gültig, solange die Auswahl gleich bleibt. */
  const [more, setMore] = useState<TimelineSelection | null>(null);
  const known = (id: string | null | undefined): id is string => rows.some((r) => r.id === id);
  // Der Tabstopp: zuletzt fokussierte Zeile, sonst die gewählte (Neuladen, Dialog zu), sonst die erste.
  const stop = [active, selection?.rowId].find(known) ?? rows[0]?.id;
  const heights = new Map(p.layout.rows.map((r) => [r.id, r.height]));

  // G5: hat die gewählte Zeile nach einem Neuladen weniger Balken, meldet die Spalte die geklemmte Wahl — so heben
  // Fläche und Tastatur denselben Balken hervor.
  const chosen = rows.find((r) => r.id === selection?.rowId);
  const fixed = chosen === undefined ? null : barAt(chosen, selection);
  const stale = chosen !== undefined && selection!.barIndex !== fixed ? `${chosen.id}#${fixed}` : "";
  const { onSelect } = p;
  useEffect(() => {
    if (stale !== "") onSelect({ rowId: chosen!.id, barIndex: fixed });
  }, [stale]);   // nur bei einer neuen Unstimmigkeit, nicht bei jedem neuen Rückruf der Ansicht
  // Der „+n“-Halt gilt nur, solange die Auswahl in seiner Zeile bleibt (Re-Review 4, N2): Wählt die Ansicht eine andere
  // Zeile (Klick, Escape, Neuladen), ist er verbraucht — sonst stünde er bei der Rückkehr auf denselben Balken wieder da.
  useEffect(() => { setMore(null); }, [selection?.rowId]);

  const say = (text: string): void => {
    const live = p.liveRef.current;
    if (live === null) return;
    if (live.textContent !== text) { live.textContent = text; return; }
    live.textContent = "";                                       // gleicher Text: leeren und im nächsten Bild setzen —
    requestAnimationFrame(() => { live.textContent = text; });  // eine polite-Region spräche ihn sonst nicht noch einmal
  };

  const keys = (e: KeyboardEvent<HTMLUListElement>): void => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;              // Browser- und Systemkürzel (Alt+← = zurück)
    const li = e.target as HTMLElement;                          // nur eine Zeile selbst — der „+n“-Knopf findet keine
    const moved = wandereFokus(e, ROW, li.ownerDocument);
    if (moved !== null) {
      if (moved !== li) {                                        // am Rand bleibt alles, wie es ist
        setMore(null);                                           // Wegwandern verbraucht den „+n“-Halt (N1)
        p.onSelect({ rowId: moved.dataset.row!, barIndex: null });
      }
      return;
    }
    const row = rows.find((r) => r.id === li.dataset.row);
    if (row === undefined) return;
    const at = barAt(row, selection);
    const atMore = more !== null && more.rowId === row.id && more.barIndex === at;
    const count = stops(row).length;
    const bar = at === null || atMore || row.kind === "DUE" ? undefined : row.bars[at];
    const marker = at === null || atMore || row.kind !== "DUE" ? undefined : row.markers[at];
    const pick = (i: number): void => {
      setMore(null);
      p.onSelect({ rowId: row.id, barIndex: i });
      if (row.kind === "DUE") { say(DUE_ITEM_TEXT.speak(row.markers[i]!.item, p.heute)); return; }
      const bar = row.bars[i]!;
      say(TIMELINE_TEXT.barSpoken(bar, p.heute, bar.duplicateOf === null ? undefined : p.groupNames?.get(bar.duplicateOf)));
    };
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const pos = atMore ? count : at;
      const next = e.key === "ArrowRight" ? (pos ?? -1) + 1 : pos === null ? -1 : pos - 1;
      if (next >= 0 && next < count) pick(next);
      else if (next === count && hiddenNumbers(row).length > 0) {
        const numbers = hiddenNumbers(row);
        setMore({ rowId: row.id, barIndex: at });
        say(row.person === null ? TIMELINE_TEXT.moreList(numbers) : TIMELINE_TEXT.moreOpen(numbers));
      }
    } else if (e.key === "Enter" && (atMore ? row.person !== null : bar !== undefined || marker !== undefined)) {
      e.preventDefault();
      if (marker !== undefined) p.onOpenDue(marker.item);
      else if (bar !== undefined) p.onOpen(bar.id); else p.onMore(row.person!);
    } else if (e.key === " " && bar?.canExpand === true) {
      e.preventDefault();
      p.onToggleBar(bar.id);
    } else if (e.key === "Escape") {
      setMore(null);
      p.onSelect(null);
    }
  };

  return (
    <section className="tl__zeilenspalte" data-group={group.id}>
      <GroupHead group={group} phone={phone} onToggle={p.onToggleGroup} />
      {!group.collapsed && (
        <ul role="list" className="tl__zeilen" aria-label={group.label} onKeyDown={keys}
          style={{ width: phone ? LABEL_PX_PHONE : LABEL_PX }} {...(phone ? { "data-telefon": "" } : {})}>
          {rows.map((r) => (
            <Row key={r.id} row={r} team={r.kind === "TEAM" ? group.team : null} height={heights.get(r.id) ?? ROW_MIN_PX}
              phone={phone} tabStop={r.id === stop} me={r.person !== null && r.person.id === p.meId}
              onFocus={() => setActive(r.id)} onMore={p.onMore} companies={p.companies} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** Der Gruppenkopf (S5): der ganze Kopf ist der Klappknopf; sein Name nennt Kürzel, Gruppe und Zahlen. Am Telefon steht
 *  nur das Kürzel, der Name kommt ins Sofort-Popup. Höhe HEADER_PX, klebt unter der Kopfachse (AXIS_PX). Termingruppen
 *  (D24) mit Symbol der Quelle statt Kürzel. */
function GroupHead(p: { group: TimelineGroup; phone: boolean; onToggle: (groupId: string) => void }): ReactElement {
  const g = p.group;
  const team = g.kind === "TEAM";
  const short = p.phone && g.team !== null;
  const popup = useSofortPopup<HTMLElement>(short ? g.label : "");
  return (
    <header className="tl__gruppenkopf" style={{ height: HEADER_PX, top: AXIS_PX }}>
      <KiKnopf art="leise" className="tl__klapp" knopfRef={popup} onClick={() => p.onToggle(g.id)} ausgeklappt={!g.collapsed}
        name={team ? TIMELINE_TEXT.groupName(g.label, g.loaded, g.loadedOverdue, g.team?.shortCode)
          : TIMELINE_TEXT.dueGroupName(g.label, g.loaded, g.loadedOverdue)}>
        <Icon name={g.collapsed ? "chevron-right" : "chevron-down"} size={12} />
        {g.team !== null && <Chip>{g.team.shortCode}</Chip>}
        {g.source !== null && <DueSymbol source={g.source} />}
        {!short && <span className="tl__gruppenname">{g.label}</span>}
        <Chip>{g.loaded}</Chip>
        {g.loadedOverdue > 0 && (
          <Chip statusArt="fehler">{(team ? TIMELINE_TEXT.overdue : TIMELINE_TEXT.dueOverdue)(g.loadedOverdue)}</Chip>
        )}
      </KiKnopf>
    </header>
  );
}

/** Eine Zeile: Avatar, Name, „+n“, Überfällig-Zahl, „frei“ — am Telefon nur der Avatar mit dem Namen im Popup. Der
 *  zugängliche Name (`rowName`) trägt alle Zahlen; `data-ticket-ids` = die Balken der Zeile (Fokus-Rückgabe). Eine
 *  Terminzeile (D24) zeigt statt Avatar und Name das Kürzel des Unternehmens in der Hausfarbe und trägt keine Tickets. */
function Row(p: { row: TimelineRow; team: TimelineGroup["team"]; height: number; phone: boolean; tabStop: boolean; me: boolean;
                  onFocus: () => void; onMore: (person: TimelinePerson) => void;
                  companies: TicketTimelineRowsProps["companies"] }): ReactElement {
  const { row, phone } = p;
  const due = row.kind === "DUE";
  const popup = useSofortPopup<HTMLSpanElement>(phone && !due ? row.label : "");
  const ohneTipp = useOhneTipp(phone);
  const hidden = hiddenNumbers(row).length;
  return (
    <li role="listitem" tabIndex={p.tabStop ? 0 : -1} data-row={row.id} data-depth={row.depth} onFocus={p.onFocus}
      aria-label={(due ? TIMELINE_TEXT.dueRowName : TIMELINE_TEXT.rowName)(row.label, row.loaded, row.loadedOverdue, hidden)}
      {...(due ? {} : { "data-ticket-ids": [...row.bars, ...row.hidden].map((b) => b.id).join(" ") })}
      style={{ height: p.height, ...(row.depth > 0 ? { "--tl-einzug": `${row.depth * INDENT_PX}px` } : {}) } as CSSProperties}>
      <span ref={popup} className="tl__avatar">
        {due ? <Chip {...hausfarbeChip(p.companies, row.label)}>{row.label}</Chip>
          : p.team !== null
          ? <MeAvatar ref={ohneTipp} art="team" name={HIERARCHY_TEXT.queueOf(p.team.name)} kuerzel={p.team.shortCode}
              nutzerId={`team:${p.team.id}`} />
          : <Avatar person={row.person} ich={p.me} ohneName tabhalt={false} ohneTipp={phone} />}
      </span>
      {!phone && <>
        {!due && <span className="tl__name">{row.label}</span>}
        {hidden > 0 && <More row={row} onMore={p.onMore} />}
        {row.loadedOverdue > 0 && <Chip statusArt="fehler">{row.loadedOverdue}</Chip>}
        {row.loaded === 0 && <span className="tl__unbelegt">{TIMELINE_TEXT.idle}</span>}
      </>}
    </li>
  );
}

/** „+n“ (S8): die verborgenen Balken; das Popup nennt ihre Nummern. An einer Personenzeile ein Knopf (Liste mit der
 *  Person) OHNE eigenen Tab-Halt — per Tastatur ist er der Halt hinter dem letzten Balken der Zeile (G3); an der
 *  Teamzeile und an Terminzeilen ein Kennzeichen, dort gibt es keine Person für den Filter. */
function More(p: { row: TimelineRow; onMore: (person: TimelinePerson) => void }): ReactElement {
  const numbers = hiddenNumbers(p.row);
  const popup = useSofortPopup<HTMLElement>(TIMELINE_TEXT.moreList(numbers));
  // Negativer tabindex am Wirt nimmt me-knopf (delegatesFocus) samt innerem Knopf aus der Tab-Folge.
  const host = useCallback((el: HTMLElement | null) => { popup(el); el?.setAttribute("tabindex", "-1"); }, [popup]);
  const person = p.row.person;
  const text = TIMELINE_TEXT.more(numbers.length);
  return person === null ? <Chip ref={popup} className="tl__mehr">{text}</Chip>
    : <KiKnopf gestalt="pille" className="tl__mehr" name={TIMELINE_TEXT.moreOpen(numbers)} knopfRef={host}
        onClick={() => p.onMore(person)}>{text}</KiKnopf>;
}
